import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineChevronLeft, HiOutlineRefresh, HiOutlineSearch, HiOutlinePlus, HiOutlineX, HiOutlineUpload, HiOutlineDownload } from 'react-icons/hi';
import { FaEdit, FaTrash } from 'react-icons/fa';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { hasCardAction } from '../../utils/permissionHelper';
import CustomerForm from './CustomerForm';
import ColumnFilter, { BLANK_VALUE } from '../common/ColumnFilter';
import ImportWizard from '../common/ImportWizard/ImportWizard';
import './CustomerRegister.css';

// Endpoints for the generic import wizard (see backend utils/importWizard.js
// for the engine and controllers/customerImportController.js for the
// customer-specific field schema/validation this wires up to).
const CUSTOMER_IMPORT_URLS = {
    upload: '/api/customers/import/preview',
    template: '/api/customers/import/template',
    validate: (id) => `/api/customers/import/${id}/validate`,
    confirm: (id) => `/api/customers/import/${id}/confirm`
};

// Columns that carry an Excel-style header filter. Keys match the backend's
// FILTERABLE_COLUMNS map.
const FILTER_COLUMNS = [
    { key: 'nationality', label: 'Nationality' },
    { key: 'zone', label: 'Zone' },
    { key: 'street', label: 'Street' },
    { key: 'building', label: 'Building' }
];

const EMPTY_COLUMN_FILTERS = { nationality: [], zone: [], street: [], building: [] };

const CustomerRegister = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [showImport, setShowImport] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Excel-style per-column filters: { nationality: ['Indian'], zone: ['1'], ... }
    // An empty array means the column is unfiltered.
    const [columnFilters, setColumnFilters] = useState(EMPTY_COLUMN_FILTERS);

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    // Admin-only tenant scope: list of companies and the branches of the picked company
    const [filters, setFilters] = useState({ company: '', branch: '' });
    const [companies, setCompanies] = useState([]);
    const [branches, setBranches] = useState([]);

    const userPermissions = user?.permissions || { cards: [], actions: [] };
    const isSystemAdmin = user?.role === 'system_admin';
    const canCreate = user?.role === 'system_admin' || hasCardAction(userPermissions, 'customer-register', 'create');
    const canEdit = user?.role === 'system_admin' || hasCardAction(userPermissions, 'customer-register', 'edit');
    const canDelete = user?.role === 'system_admin' || hasCardAction(userPermissions, 'customer-register', 'delete');

    // Admin only: load the list of companies once for the scope filter.
    useEffect(() => {
        if (!isSystemAdmin) return;
        const fetchCompanies = async () => {
            try {
                const response = await axiosInstance.get('/api/tenants');
                if (response.data.success) {
                    setCompanies(response.data.tenants || []);
                }
            } catch (error) {
                console.error('Error fetching companies:', error);
            }
        };
        fetchCompanies();
    }, [isSystemAdmin]);

    // Admin only: when a company is picked, load its branches. Clearing the
    // company empties the branch list.
    useEffect(() => {
        if (!isSystemAdmin) return;
        if (!filters.company) {
            setBranches([]);
            return;
        }
        const fetchBranches = async () => {
            try {
                const response = await axiosInstance.get('/api/tenants', {
                    params: { company: filters.company }
                });
                if (response.data.success) {
                    setBranches(response.data.tenants || []);
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
            }
        };
        fetchBranches();
    }, [filters.company, isSystemAdmin]);

    // Picking a company resets the branch and jumps back to page 1.
    const handleCompanyChange = (e) => {
        setFilters(prev => ({ ...prev, company: e.target.value, branch: '' }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleBranchChange = (e) => {
        setFilters(prev => ({ ...prev, branch: e.target.value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleClearScope = () => {
        setFilters({ company: '', branch: '' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Column filters send `?zone=1,5,12`; blanks use the server's __BLANK__ sentinel.
    const columnFilterParams = useMemo(() => {
        const params = {};
        FILTER_COLUMNS.forEach(({ key }) => {
            const values = columnFilters[key] || [];
            if (values.length > 0) params[key] = values.join(',');
        });
        return params;
    }, [columnFilters]);

    // Context handed to each header dropdown so its value list narrows the same
    // way the table does. The server drops the column's own filter from this.
    const filterOptionParams = useMemo(() => ({
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(filters.company ? { company: filters.company } : {}),
        ...(filters.branch ? { branch: filters.branch } : {}),
        ...columnFilterParams
    }), [debouncedSearch, filters.company, filters.branch, columnFilterParams]);

    const activeFilterCount = useMemo(
        () => FILTER_COLUMNS.reduce((sum, { key }) => sum + (columnFilters[key]?.length || 0), 0),
        [columnFilters]
    );

    // Debounce typing in the search box; every other input applies immediately.
    useEffect(() => {
        const delaySearch = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
        }, 500);
        return () => clearTimeout(delaySearch);
    }, [searchTerm]);

    useEffect(() => {
        fetchCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, filters, columnFilterParams, pagination.page, pagination.limit]);

    const handleColumnFilterChange = (key, values) => {
        setColumnFilters(prev => ({ ...prev, [key]: values }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleClearColumnFilters = () => {
        setColumnFilters(EMPTY_COLUMN_FILTERS);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const fetchCustomers = async () => {
        try {
            setLoading(true);
            const params = {
                search: debouncedSearch,
                ...filters,
                ...columnFilterParams,
                page: pagination.page,
                limit: pagination.limit
            };
            Object.keys(params).forEach(key => !params[key] && delete params[key]);

            const response = await axiosInstance.get('/api/customers', { params });
            if (response.data.success) {
                setCustomers(response.data.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.total || 0,
                    pages: response.data.pages || 0
                }));
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            alert('Failed to fetch customers');
        } finally {
            setLoading(false);
        }
    };

    // Exports exactly what's currently on screen: same search + column filters
    // + company/branch scope as the list request.
    const handleExport = async (format) => {
        setExporting(true);
        try {
            const params = {
                search: debouncedSearch,
                ...filters,
                ...columnFilterParams,
                format
            };
            Object.keys(params).forEach(key => !params[key] && delete params[key]);

            const response = await axiosInstance.get('/api/customers/export', {
                params,
                responseType: 'blob'
            });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `customers-export-${new Date().toISOString().slice(0, 10)}.${format}`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting customers:', error);
            alert('Failed to export customers');
        } finally {
            setExporting(false);
        }
    };

    const handleImported = () => {
        setShowImport(false);
        fetchCustomers();
    };

    const handleAddNew = () => {
        setEditingCustomer(null);
        setShowForm(true);
    };

    const handleEdit = (customer) => {
        setEditingCustomer(customer);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this customer?')) return;

        try {
            await axiosInstance.delete(`/api/customers/${id}`);
            alert('Customer deleted successfully');
            fetchCustomers();
        } catch (error) {
            console.error('Error deleting customer:', error);
            alert('Failed to delete customer');
        }
    };

    const handleFormClose = (refresh) => {
        setShowForm(false);
        setEditingCustomer(null);
        if (refresh) fetchCustomers();
    };

    if (loading && customers.length === 0) {
        return (
            <div className="cr-shell">
                <div className="cr-loading"><div className="spinner"></div>Loading customers...</div>
            </div>
        );
    }

    return (
        <div className="cr-shell">
            {/* HEADER */}
            <header className="cr-header">
                <div className="cr-header-left">
                    <button className="cr-back-btn" onClick={() => navigate('/purpose-selection')}>
                        <HiOutlineChevronLeft size={20} />
                    </button>
                    <div className="cr-header-title">
                        <h1>Customer Register</h1>
                        <span>Module</span>
                        {user?.company?.name && (
                            <span className="cr-company-badge">🏢 {user.company.name}</span>
                        )}
                    </div>
                </div>
                <div className="cr-header-right">
                    {canCreate && (
                        <button className="btn btn-primary btn-sm" onClick={handleAddNew}>
                            <HiOutlinePlus /> Add Customer
                        </button>
                    )}
                    {canCreate && (
                        <button className="btn btn-secondary btn-sm" onClick={() => setShowImport(true)}>
                            <HiOutlineUpload /> Import
                        </button>
                    )}
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleExport('xlsx')}
                        disabled={exporting}
                    >
                        <HiOutlineDownload /> {exporting ? 'Exporting...' : 'Export'}
                    </button>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={fetchCustomers}
                        disabled={loading}
                    >
                        <HiOutlineRefresh /> {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </header>

            {/* BODY */}
            <main className="cr-content">
                <div className="cr-filters-section">
                    {/* Admin-only: choose whose data to view (company / branch) */}
                    {isSystemAdmin && (
                        <div className="cr-admin-scope">
                            <span className="cr-admin-scope-label">🏢 Viewing data for:</span>
                            <select
                                value={filters.company}
                                onChange={handleCompanyChange}
                                className="cr-filter-select"
                            >
                                <option value="">All Companies</option>
                                {companies.map((c) => (
                                    <option key={c._id} value={c._id}>{c.name}</option>
                                ))}
                            </select>
                            <select
                                value={filters.branch}
                                onChange={handleBranchChange}
                                className="cr-filter-select"
                                disabled={!filters.company}
                            >
                                <option value="">
                                    {filters.company ? 'All Branches' : 'Select company first'}
                                </option>
                                {branches.map((b) => (
                                    <option key={b._id} value={b._id}>{b.name}</option>
                                ))}
                            </select>
                            {(filters.company || filters.branch) && (
                                <button
                                    onClick={handleClearScope}
                                    className="btn btn-secondary btn-sm"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    )}
                    <div className="cr-toolbar">
                        <div className="cr-search-wrapper">
                            <HiOutlineSearch className="cr-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by name, mobile, or customer ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="cr-search-input"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    className="cr-search-clear"
                                    onClick={() => setSearchTerm('')}
                                    title="Clear search"
                                >
                                    <HiOutlineX size={16} />
                                </button>
                            )}
                        </div>

                        {/* Below 640px the table collapses to cards and the header
                            row (with its funnels) is hidden — surface the same
                            filters here instead. */}
                        <div className="cr-mobile-filters">
                            {FILTER_COLUMNS.map(({ key, label }) => (
                                <span key={key} className="cr-mobile-filter">
                                    {label}
                                    <ColumnFilter
                                        column={key}
                                        label={label}
                                        selected={columnFilters[key]}
                                        onChange={(values) => handleColumnFilterChange(key, values)}
                                        queryParams={filterOptionParams}
                                        endpoint="/api/customers/filter-options"
                                    />
                                </span>
                            ))}
                        </div>

                        {/* One chip per filtered column — mirrors the header dropdowns */}
                        {activeFilterCount > 0 && (
                            <div className="cr-filter-chips">
                                {FILTER_COLUMNS.map(({ key, label }) => {
                                    const values = columnFilters[key] || [];
                                    if (values.length === 0) return null;
                                    const preview = values.length <= 2
                                        ? values.map(v => (v === BLANK_VALUE ? '(Blanks)' : v)).join(', ')
                                        : `${values.length} selected`;
                                    return (
                                        <span key={key} className="cr-filter-chip">
                                            <strong>{label}:</strong> {preview}
                                            <button
                                                type="button"
                                                onClick={() => handleColumnFilterChange(key, [])}
                                                title={`Clear ${label} filter`}
                                            >
                                                <HiOutlineX size={13} />
                                            </button>
                                        </span>
                                    );
                                })}
                                <button
                                    type="button"
                                    className="cr-clear-all-filters"
                                    onClick={handleClearColumnFilters}
                                >
                                    Clear all filters
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Results Info */}
                <div className="cr-results-info">
                    <span>
                        Showing {customers.length > 0 ? ((pagination.page - 1) * pagination.limit + 1) : 0}
                        {' '}-{' '}
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                        {' '}of {pagination.total} customers
                    </span>
                    <div className="cr-page-size">
                        <label>Items per page:</label>
                        <select
                            value={pagination.limit}
                            onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                            className="cr-page-size-select"
                        >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                </div>

                {/* Table */}
                <div className="cr-table-wrap">
                    <div className="cr-table-container">
                        <table className="cr-table">
                            <thead>
                                <tr>
                                    <th>Actions</th>
                                    <th>Customer ID</th>
                                    <th>Name</th>
                                    <th>Mobile</th>
                                    {FILTER_COLUMNS.map(({ key, label }) => (
                                        <th key={key}>
                                            <span className="cr-th-inner">
                                                {label}
                                                <ColumnFilter
                                                    column={key}
                                                    label={label}
                                                    selected={columnFilters[key]}
                                                    onChange={(values) => handleColumnFilterChange(key, values)}
                                                    queryParams={filterOptionParams}
                                                    endpoint="/api/customers/filter-options"
                                                />
                                            </span>
                                        </th>
                                    ))}
                                    <th>Other Address</th>
                                    <th>Salesman</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="cr-empty-table">
                                            No customers found
                                        </td>
                                    </tr>
                                ) : (
                                    customers.map((customer) => (
                                        <tr key={customer._id}>
                                            <td data-label="Actions">
                                                <div className="cr-action-buttons">
                                                    {canEdit && (
                                                        <button
                                                            onClick={() => handleEdit(customer)}
                                                            className="cr-action-icon cr-edit-icon"
                                                            title="Edit customer"
                                                        >
                                                            <FaEdit size={16} />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            onClick={() => handleDelete(customer._id)}
                                                            className="cr-action-icon cr-delete-icon"
                                                            title="Delete customer"
                                                        >
                                                            <FaTrash size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td data-label="Customer ID"><strong>{customer.customerId}</strong></td>
                                            <td data-label="Name">{customer.name}</td>
                                            <td data-label="Mobile">📱 {customer.mobileNo}</td>
                                            <td data-label="Nationality">{customer.nationality}</td>
                                            <td data-label="Zone">{customer.zone || '-'}</td>
                                            <td data-label="Street">{customer.street || '-'}</td>
                                            <td data-label="Building">{customer.building || '-'}</td>
                                            <td data-label="Other Address" className="cr-other-address">
                                                {customer.otherAddress || '-'}
                                            </td>
                                            <td data-label="Salesman">
                                                {customer.salesman ? `${customer.salesman.name} (${customer.salesman.employeeId})` : '-'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="cr-pagination">
                        <button
                            className="cr-page-btn"
                            onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                            disabled={pagination.page === 1}
                        >
                            First
                        </button>
                        <button
                            className="cr-page-btn"
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={pagination.page === 1}
                        >
                            Previous
                        </button>

                        <div className="cr-page-numbers">
                            {[...Array(pagination.pages)].map((_, idx) => {
                                const pageNum = idx + 1;
                                if (
                                    pageNum === 1 ||
                                    pageNum === pagination.pages ||
                                    (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                                ) {
                                    return (
                                        <button
                                            key={pageNum}
                                            className={`cr-page-num ${pagination.page === pageNum ? 'active' : ''}`}
                                            onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                } else if (
                                    pageNum === pagination.page - 3 ||
                                    pageNum === pagination.page + 3
                                ) {
                                    return <span key={pageNum} className="cr-page-ellipsis">...</span>;
                                }
                                return null;
                            })}
                        </div>

                        <button
                            className="cr-page-btn"
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={pagination.page === pagination.pages}
                        >
                            Next
                        </button>
                        <button
                            className="cr-page-btn"
                            onClick={() => setPagination(prev => ({ ...prev, page: pagination.pages }))}
                            disabled={pagination.page === pagination.pages}
                        >
                            Last
                        </button>
                    </div>
                )}
            </main>

            {showForm && (
                <CustomerForm
                    customer={editingCustomer}
                    onClose={handleFormClose}
                />
            )}

            {showImport && (
                <ImportWizard
                    title="Import Customers"
                    urls={CUSTOMER_IMPORT_URLS}
                    onClose={() => setShowImport(false)}
                    onImported={handleImported}
                />
            )}
        </div>
    );
};

export default CustomerRegister;
